<script setup lang="ts">
   import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
   import { XMarkIcon } from '@heroicons/vue/20/solid';
   import type { RouteLocationRaw } from 'vue-router';
   const props = withDefaults(
      defineProps<{
         options: {
            name: string;
            to?: RouteLocationRaw | null;
            fn?: (event: MouseEvent) => void;
            prependIcon?: Function;
            appendIcon?: Function;
            class?: string;
            iconColor?: string;
         }[];
         width?: string;
         location?: string;
         mobileMenu?: boolean;
         mobileMenuTitle?: string;
         block?: boolean;
      }>(),
      {
         width: 'min-w-44',
         location: 'end-0',
         mobileMenu: false,
      }
   );
</script>
<template>
   <div>
      <Menu
         as="div"
         class="relative"
         :class="block ? 'w-full' : ''"
         v-slot="{ open }"
      >
         <MenuButton :class="block ? 'w-full' : ''">
            <slot></slot>
         </MenuButton>
         <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
         >
            <MenuItems
               :class="`${width} ${location} ${mobileMenu ? 'hidden sm:block' : ''}`"
               class="absolute z-50 mt-2 rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none"
            >
               <MenuItem
                  v-slot="{ active }"
                  v-for="option in options"
                  :key="option.name"
                  :data-test="option.id"
                  :id="option.id"
               >
                  <div
                     class="flex items-center w-full"
                     :class="[
                        active ? 'bg-gray-50' : '',
                        'w-full px-2 py-3 text-sm   text-gray-80',
                        option.class,
                     ]"
                  >
                     <component
                        v-if="option.prependIcon"
                        :is="option.prependIcon"
                        class="me-2 w-4 h-4"
                        :class="option.iconColor || ''"
                     />
                     <NuxtLink v-if="option.to" class="w-full" :to="option.to">
                        {{ option.name }}
                     </NuxtLink>
                     <div
                        v-else-if="option.fn"
                        @click="option.fn"
                        class="cursor-pointer w-full"
                     >
                        {{ option.name }}
                     </div>
                     <div v-else class="cursor-pointer w-full">
                        {{ option.name }}
                     </div>
                     <component
                        v-if="option.appendIcon"
                        :is="option.appendIcon"
                        class="ms-2 w-4 h-4"
                        :class="option.iconColor || ''"
                     />
                  </div>
               </MenuItem>
            </MenuItems>
         </transition>
         <transition name="slide-up">
            <div
               v-if="mobileMenu && open"
               class="sm:hidden fixed bottom-0 start-0 w-full bg-white rounded-t-2xl z-50"
            >
               <MenuItems>
                  <div>
                     <div
                        class="border-b border-b-gray-25"
                        v-if="mobileMenuTitle"
                     >
                        <div class="flex justify-between items-center p-4">
                           <div>{{ mobileMenuTitle }}</div>
                           <XMarkIcon class="w-6 h-6 cursor-pointer" />
                        </div>
                     </div>
                     <div
                        class="px-8 pb-8"
                        :class="!mobileMenuTitle ? 'pt-4' : ''"
                     >
                        <MenuItem
                           v-slot="{ active, close }"
                           v-for="option in options"
                           :key="option.name"
                        >
                           <div
                              class="flex items-center w-full"
                              :class="[
                                 active ? 'bg-gray-50' : '',
                                 'w-full px-2 py-3 text-sm   text-gray-80',
                                 option.class,
                              ]"
                           >
                              <component
                                 v-if="option.prependIcon"
                                 :is="option.prependIcon"
                                 class="me-2 w-4 h-4"
                                 :class="option.iconColor || ''"
                              />
                              <NuxtLink
                                 v-if="option.to"
                                 class="w-full"
                                 :to="option.to"
                                 @click.prevent="close()"
                              >
                                 {{ option.name }}
                              </NuxtLink>
                              <div
                                 v-else-if="option.fn"
                                 @click="option.fn"
                                 class="cursor-pointer w-full z-50"
                              >
                                 {{ option.name }}
                              </div>
                              <div v-else class="cursor-pointer w-full">
                                 {{ option.name }}
                              </div>
                              <component
                                 v-if="option.appendIcon"
                                 :is="option.appendIcon"
                                 class="ms-2 w-4 h-4"
                                 :class="option.iconColor || ''"
                              />
                           </div>
                        </MenuItem>
                        <slot name="footer"></slot>
                     </div>
                  </div>
               </MenuItems>
            </div>
         </transition>
         <div
            v-if="mobileMenu && open"
            class="sm:hidden fixed top-0 start-0 w-full h-full bg-[#344054]/50 z-40"
         ></div>
      </Menu>
   </div>
</template>
